import React, { useState, useEffect, useRef } from "react";
import { Button, Form, Modal, Icon, Grid } from "semantic-ui-react";
import PropTypes from "prop-types";
import { get, includes, find, reject, isEmpty } from "lodash";

// External Components
import TextareaAutosize from "react-textarea-autosize";

// Components
import Input from "@Components/Input";
import DraggableMap from "@Components/DraggableMap";
import { phoneFormat } from "@Utils/formats";

// Styles
import styles from "./styles.module.css";

const MapAddressSelector = ({ onClose = () => {}, onSelect = () => {}, defaultData = {}, withForm = true }) => {
	const [title, setTitle] = useState("");
	const [referenceToArrive, setReferenceToArrive] = useState("");
	const [province, setProvince] = useState("");
	const [city, setCity] = useState("");
	const [sector, setSector] = useState("");
	const [zone, setZone] = useState("");
	const [street, setStreet] = useState("");
	const [streetNumber, setStreetNumber] = useState("");
	const [postalCode, setPostalCode] = useState("");
	const [geocodeData, setGeocodeData] = useState("");
	const [location, setLocation] = useState({ lat: 18.461174, lng: -69.941578 });
	const [defaultFormattedAddress, setDefaultFormattedAddress] = useState("");
	const [deliveryRestrictions, setDeliveryRestrictions] = useState("");
	const [personInCharge, setPersonInCharge] = useState("");
	const [phone, setPhone] = useState("");
	const [phone2, setPhone2] = useState("");
	const mapRef = useRef();

	useEffect(() => {
		if (!isEmpty(defaultData)) {
			setTitle(get(defaultData, "title", ""));
			setReferenceToArrive(get(defaultData, "referenceToArrive", ""));
			setProvince(get(defaultData, "province", ""));
			setCity(get(defaultData, "city", ""));
			setSector(get(defaultData, "sector", ""));
			setZone(get(defaultData, "zone", ""));
			setStreet(get(defaultData, "street", ""));
			setStreetNumber(get(defaultData, "streetNumber", ""));
			setPostalCode(get(defaultData, "postalCode", ""));
			setLocation(get(defaultData, "location", {}));
			setDefaultFormattedAddress(get(defaultData, "formattedAddress", ""));
			setDeliveryRestrictions(get(defaultData, "deliveryRestrictions", ""));
			setPersonInCharge(get(defaultData, "personInCharge", ""));
			setPhone(get(defaultData, "phone", ""));
			setPhone2(get(defaultData, "phone2", ""));
		}
	}, []);

	const addressFormatter = () => {
		let formattedAddress = `${street} ${streetNumber}, ${city}, ${province}`;
		if (!isEmpty(street)) {
			formattedAddress = street;
		}

		if (!isEmpty(streetNumber)) {
			formattedAddress = `${formattedAddress} ${streetNumber}`;
		}

		if (!isEmpty(zone)) {
			formattedAddress = `${formattedAddress}, ${zone}`;
		}

		if (!isEmpty(sector)) {
			formattedAddress = `${formattedAddress}, ${sector}`;
		}

		if (!isEmpty(city)) {
			formattedAddress = `${formattedAddress}, ${city}`;
		}

		if (!isEmpty(province)) {
			formattedAddress = `${formattedAddress}, ${province}`;
		}

		return formattedAddress;
	};

	// handle save
	const handlerSelect = () => {
		const formattedAddress = addressFormatter();
		const addressDataToSave = {
			...defaultData,
			title,
			referenceToArrive,
			province,
			city,
			sector,
			zone,
			street,
			streetNumber,
			postalCode,
			formattedAddress,

			deliveryRestrictions,
			personInCharge,
			phone,
			phone2,

			location: !isEmpty(geocodeData) ? get(geocodeData, "geometry.location", {}) : location,
			latitude: !isEmpty(geocodeData) ? get(geocodeData, "geometry.location.lat", "") : location.lat,
			longitude: !isEmpty(geocodeData) ? get(geocodeData, "geometry.location.lng", "") : location.lng,
		};
		onSelect(addressDataToSave);
	};

	const geocodeDataHandler = (item) => {
		setGeocodeData(item);
		const addressComponents = get(item, "address_components", []);

		const streetNoFilter = find(addressComponents, (addressItem) => {
			return includes(addressItem.types, "street_number");
		});

		const streetFilter = find(addressComponents, (addressItem) => {
			return includes(addressItem.types, "route");
		});

		const sectorFilter = find(addressComponents, (addressItem) => {
			return includes(addressItem.types, "administrative_area_level_2");
		});

		const zoneFilter = find(addressComponents, (addressItem) => {
			return includes(addressItem.types, "sublocality_level_1");
		});

		const cityFilter = find(addressComponents, (addressItem) => {
			return includes(addressItem.types, "locality");
		});

		const provinceFilter = find(addressComponents, (addressItem) => {
			return includes(addressItem.types, "administrative_area_level_1");
		});

		const postalCodeFilter = find(addressComponents, (addressItem) => {
			return includes(addressItem.types, "postal_code");
		});

		setPostalCode(get(postalCodeFilter, "long_name", ""));
		setStreetNumber(get(streetNoFilter, "long_name", ""));
		setStreet(get(streetFilter, "long_name", ""));
		setZone(get(zoneFilter, "long_name", ""));
		setSector(get(sectorFilter, "long_name", ""));
		setCity(get(cityFilter, "long_name", ""));
		setProvince(get(provinceFilter, "long_name", ""));
		setLocation({
			lat: get(item, "geometry.location.lat", ""),
			lng: get(item, "geometry.location.lng", ""),
		});
	};
	// city, street, sector, streetNumber
	const getDisabled = () => {
		return !isEmpty(
			reject([province, city, street, sector, streetNumber], (v) => {
				return String(v || "").trim().length > 0;
			})
		);
	};

	return (
		<Modal className={styles.modalFirst} open closeOnDimmerClick={false}>
			<Modal.Header className={styles.modalHeader}>
				<Icon name="map marker alternate" />
				Seleccionar Ubicación
			</Modal.Header>
			<Modal.Content className={styles.modalContent}>
				<div className={styles.infoBanner}>
					<Icon name="map marker alternate" />
					Ingresa tu dirección o ingresa una ubicación cercana. Luego arrastra el mapa para señalar tu dirección precisa.
				</div>
				<Grid className={styles.grid} stackable>
					<Grid.Row columns={withForm ? 2 : 1}>
						<Grid.Column ref={mapRef}>
							<DraggableMap
								setGeocodeData={geocodeDataHandler}
								initialCenter={location}
								initialAddress={defaultFormattedAddress}
								height={mapRef?.current?.clientHeight}
							/>
						</Grid.Column>
						{withForm && (
							<Grid.Column>
								<Form className={styles.form}>
									<Input
										fluid
										value={title}
										icon="map"
										placeholder="Título"
										onChange={(e, { value }) => {
											setTitle(value);
										}}
									/>
									<div className={styles.rowInputContainer}>
										<Input
											label="Provincia"
											required
											fluid
											value={province}
											icon="map"
											placeholder="Provincia"
											onChange={(e, { value }) => {
												setProvince(value);
											}}
										/>
										<Input
											label="Ciudad"
											required
											fluid
											value={city}
											icon="map"
											placeholder="Ciudad"
											onChange={(e, { value }) => {
												setCity(value);
											}}
										/>
									</div>
									<div className={styles.rowInputContainer}>
										<Input
											label="Sector"
											required
											fluid
											value={sector}
											icon="map"
											placeholder="Sector"
											onChange={(e, { value }) => {
												setSector(value);
											}}
										/>
										<Input
											label="Zona"
											fluid
											value={zone}
											icon="map"
											placeholder="Zona"
											onChange={(e, { value }) => {
												setZone(value);
											}}
										/>
									</div>
									<div className={styles.rowInputContainer}>
										<Input
											required
											label="Calle"
											fluid
											value={street}
											icon="map"
											placeholder="Calle"
											onChange={(e, { value }) => {
												setStreet(value);
											}}
										/>
										<Input
											required
											label="Número de casa"
											fluid
											value={streetNumber}
											icon="map"
											placeholder="Número de casa"
											onChange={(e, { value }) => {
												setStreetNumber(value);
											}}
										/>
									</div>
									<Input
										label="Código Postal"
										fluid
										className={styles.postalCodeInput}
										value={postalCode}
										icon="globe"
										placeholder="Código Postal"
										onChange={(e, { value }) => {
											setPostalCode(value);
										}}
									/>
									<span className={styles.customLabel}>Referencias para llegar:</span>
									<TextareaAutosize
										placeholder="Puedes agregar cualquier información adicional que nos pueda servir para llegar más rápido (entre qué calles estás, alguna referencia, etc.)"
										value={referenceToArrive}
										onChange={(e) => {
											setReferenceToArrive(e.target.value);
										}}
										className={styles.textArea}
									/>
									<span className={styles.customLabel}>Restricciones de entrega:</span>
									<TextareaAutosize
										placeholder="Escribir..."
										value={deliveryRestrictions}
										onChange={(e) => {
											setDeliveryRestrictions(e.target.value);
										}}
										className={styles.textArea}
									/>
									<Input
										fluid
										value={personInCharge}
										icon="user"
										placeholder="Persona que Recibe:"
										onChange={(e, { value }) => {
											setPersonInCharge(value);
										}}
									/>
									<div className={styles.rowInputContainer}>
										<Input
											fluid
											value={phoneFormat(phone)}
											icon="map"
											placeholder="Teléfono 1"
											onChange={(e, { value }) => {
												setPhone(value);
											}}
										/>
										<Input
											fluid
											value={phoneFormat(phone2)}
											icon="map"
											placeholder="Teléfono 2"
											onChange={(e, { value }) => {
												setPhone2(value);
											}}
										/>
									</div>
								</Form>
							</Grid.Column>
						)}
					</Grid.Row>
				</Grid>
			</Modal.Content>
			<Modal.Actions className={styles.modalFooter}>
				<Button className={styles.negativeButton} onClick={onClose}>
					Atrás
				</Button>
				<Button className={styles.positiveButton} disabled={getDisabled()} onClick={handlerSelect}>
					Seleccionar
				</Button>
			</Modal.Actions>
		</Modal>
	);
};

MapAddressSelector.propTypes = {
	onClose: PropTypes.func,
	onSelect: PropTypes.func,
	defaultData: PropTypes.object,
	withForm: PropTypes.bool,
};

export default MapAddressSelector;
